import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import apiService from '@/api/service'
import type { StoreAnalyzeResponse } from '@/types/api'
import { Food } from '@/components/Food'
import { StarRatingChart } from '@/components/StarRatingChart'
import { SentimentChart } from '@/components/SentimentChart'
import { TrendChart } from '@/components/TrendChart'

export const AnalyzeContent = (): JSX.Element => {
    const navigate = useNavigate()
    const [data, setData] = useState<StoreAnalyzeResponse | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string>('')
    const [showCategoryNote, setShowCategoryNote] = useState(false)
    const [showKeywordNote, setShowKeywordNote] = useState(false)

    useEffect(() => {
        const fetchData = async () => {
            try {
                // 使用真實 API 抓取資料
                const storeCode = apiService.getCurrentStoreCode()
                const token = apiService.getCurrentToken()

                if (!storeCode || !token) {
                    console.warn('未找到 store_code 或 token，跳轉到登入頁面')
                    navigate('/login')
                    return
                }

                console.log('正在從 API 獲取店家分析資料...')
                const response = await apiService.getStoreAnalyze({
                    store_code: storeCode,
                    argameusertoken: token
                })

                console.log('店家分析資料獲取成功:', response)

                // 檢查是否為 token 失效錯誤
                if (response && response.status === 'failed' && 
                    response.message && response.message.includes('This tokens is invalid, please login again')) {
                    console.warn('Token 已失效，清除登入資料並跳轉到登入頁面')
                    
                    // 清除所有登入相關資料
                    apiService.logout()
                    
                    // 跳轉到登入頁面
                    navigate('/login')
                    return
                }

                // 驗證資料結構
                if (!response || !response.data || !response.data.commentsAnalyze) {
                    console.error('API 返回的資料結構不正確:', response)
                    throw new Error('API 返回的資料結構不完整，缺少 data.commentsAnalyze')
                }

                // 如果 API 返回了店家資訊，保存到 localStorage
                if (response.data.store) {
                    console.log('儲存店家資訊:', response.data.store)
                    apiService.saveStoreInfo(response.data.store)
                    // 觸發自定義事件通知其他組件店家資訊已更新
                    window.dispatchEvent(new CustomEvent('storeInfoUpdated', {
                        detail: response.data.store
                    }))
                }

                setData(response)

                /* ===== 測試資料（已停用）===== */
                // const mockData: StoreAnalyzeResponse = {
                //     message: '',
                //     status: 'success',
                //     timestamp: '2025-09-30T10:25:40.945000',
                //     data: {
                //         storeName: '美味餐廳',
                //         overview: {
                //             commentsTotal: 256,
                //             commentsMoM: 15,
                //             monthIncreased: 12,
                //             increasedMoM: 20,
                //             monthNotReply: 5,
                //             comments: []
                //         },
                //         commentsAnalyze: {
                //             category: {
                //                 positive: [
                //                     { name: '食材新鮮度', specificGravity: 72 },
                //                     { name: '餐點口感', specificGravity: 55 },
                //                     { name: '環境舒適度', specificGravity: 80 },
                //                     { name: '菜單多樣性', specificGravity: 72 },
                //                     { name: '等候時間', specificGravity: 72 }
                //                 ],
                //                 negative: [
                //                     { name: '食材新鮮度', specificGravity: 28 },
                //                     { name: '餐點口感', specificGravity: 45 },
                //                     { name: '環境舒適度', specificGravity: 20 },
                //                     { name: '菜單多樣性', specificGravity: 28 },
                //                     { name: '等候時間', specificGravity: 28 }
                //                 ]
                //             },
                //             keywords: {
                //                 positive: [
                //                     { name: '新鮮', commentsNum: 72 },
                //                     { name: '美味', commentsNum: 55 },
                //                     { name: '舒適', commentsNum: 80 },
                //                     { name: '多樣', commentsNum: 72 },
                //                     { name: '快速', commentsNum: 72 }
                //                 ],
                //                 negative: [
                //                     { name: '不新鮮', commentsNum: 28 },
                //                     { name: '不美味', commentsNum: 45 },
                //                     { name: '不舒適', commentsNum: 20 },
                //                     { name: '單調', commentsNum: 28 },
                //                     { name: '慢', commentsNum: 28 }
                //                 ]
                //             },
                //             average: {
                //                 comments: [
                //                     { rating: 1.0, specificGravity: 2 },
                //                     { rating: 2.0, specificGravity: 3 },
                //                     { rating: 3.0, specificGravity: 17 },
                //                     { rating: 4.0, specificGravity: 23 },
                //                     { rating: 5.0, specificGravity: 55 }
                //                 ],
                //                 sentiment: {
                //                     positive: 75,
                //                     negative: 25
                //                 }
                //             },
                //             lineChart: {
                //                 positive: [
                //                     { sum: 8 },
                //                     { sum: 7 },
                //                     { sum: 6 },
                //                     { sum: 5 },
                //                     { sum: 9 },
                //                     { sum: 6 },
                //                     { sum: 3 }
                //                 ],
                //                 negative: [
                //                     { sum: 4 },
                //                     { sum: 5 },
                //                     { sum: 4 },
                //                     { sum: 3 },
                //                     { sum: 2 },
                //                     { sum: 2 },
                //                     { sum: 3 }
                //                 ]
                //             }
                //         }
                //     }
                // }
                // console.log('評論分析使用 API 文件中的測試資料')
                // setData(mockData)
                /* ===== 測試資料結束 ===== */
            } catch (error) {
                console.error('Failed to fetch store analyze:', error)
                const errorMessage = error instanceof Error ? error.message : '載入資料失敗'
                setError(errorMessage)
            } finally {
                setIsLoading(false)
            }
        }

        fetchData()
    }, [navigate])

    if (isLoading) {
        return (
            <div className="flex flex-col w-full max-w-[1200px] items-center justify-center gap-5 pt-[50px] pb-20 px-4 md:px-[120px] relative bg-white rounded-[0px_20px_20px_20px] min-h-[600px]">
                <div className="text-[#373737] text-xl">載入中...</div>
            </div>
        )
    }

    if (error || !data) {
        return (
            <div className="flex flex-col w-full max-w-[1200px] items-center justify-center gap-5 pt-[50px] pb-20 px-4 md:px-[120px] relative bg-white rounded-[0px_20px_20px_20px] min-h-[600px]">
                <div className="text-red-500 text-xl">{error || '無法載入資料'}</div>
            </div>
        )
    }

    const { commentsAnalyze, overview } = data.data

    // 點擊分類/關鍵字跳轉到詳情頁面
    const handleCategoryClick = (categoryName: string, type: 'category' | 'keywords', sentiment: 'positive' | 'negative') => {
        const storeCode = apiService.getCurrentStoreCode()
        if (!storeCode) {
            console.error('未找到 store_code')
            return
        }

        // 傳遞所有必要參數到 Details 頁面
        const params = new URLSearchParams({
            name: categoryName,
            type: type,
            sentiment: sentiment,
            store_code: storeCode
        })

        navigate(`/details?${params.toString()}`)
    }

    // 將 API 資料轉換為組件所需格式
    const categoriesData = {
        positive: commentsAnalyze.category.positive.map(item => ({
            tagName: item.name,
            percentNumber: item.specificGravity.toString(),
            prop: '正向評論',
            prop1: '%'
        })),
        negative: commentsAnalyze.category.negative.map(item => ({
            tagName: item.name,
            percentNumber: item.specificGravity.toString(),
            prop: '負向評論',
            prop1: '%'
        }))
    }

    const keywordsData = {
        positive: commentsAnalyze.keywords.positive.map(item => ({
            tagName: item.name,
            percentNumber: item.commentsNum.toString(),
            prop: '',
            prop1: '則'
        })),
        negative: commentsAnalyze.keywords.negative.map(item => ({
            tagName: item.name,
            percentNumber: item.commentsNum.toString(),
            prop: '',
            prop1: '則'
        }))
    }

    return (
        <div className="flex flex-col w-full max-w-[95%] md:max-w-[1200px] items-center justify-center gap-4 md:gap-5 pt-0 pb-12 md:pb-20 px-4 md:px-[60px] relative bg-white rounded-[0px_20px_20px_20px]">
            {/* 評論分類區域 */}
            <div className="inline-flex flex-col items-start justify-start px-4 md:px-6 py-4 md:py-6 mt-8 md:mt-12 relative w-full max-w-[1140px] bg-[#eaeade] rounded-[15px] md:rounded-[20px] overflow-hidden">
                {/* 標題和註解 */}
                <div className="flex items-start justify-between w-full gap-3 mb-4">
                    <div className="flex items-center gap-3 md:gap-[11px]">
                        <div className="relative w-[36px] h-[36px] md:w-[46px] md:h-[46px] flex-shrink-0">
                            <img
                                className="absolute w-[73.32%] h-[50.00%] top-[25.00%] left-[12.50%]"
                                alt="Subtract"
                                src="/img/subtract.svg"
                            />
                        </div>
                        <div className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-[#373737] text-xl md:text-[28px] tracking-[2.00px] md:tracking-[2.80px]">
                            評論分類
                        </div>
                    </div>
                    <div className="flex items-start gap-2 flex-1 justify-end">
                        <button
                            onClick={() => setShowCategoryNote(!showCategoryNote)}
                            className="flex-shrink-0 w-5 h-5 hover:opacity-70 transition-opacity mt-1"
                            aria-label="顯示註解"
                        >
                            <img src="/img/🛈.png" alt="資訊" className="w-full h-full" />
                        </button>
                        {showCategoryNote && (
                            <div className="[font-family:'Noto_Sans_TC',Helvetica] font-normal text-[#666666] text-xs md:text-sm tracking-[0.80px] md:tracking-[1.00px] leading-relaxed bg-transparent max-w-[600px] line-clamp-2">
                                同則評論內容可能涵蓋多個主題分類,因此各分類的百分比為獨立計算 範例:等候時間 72% 正向=在全部評論中,有72%曾正面提到等候時間
                            </div>
                        )}
                    </div>
                </div>

                {/* 正向評論區塊 */}
                <div className="w-[calc(100%+2rem)] md:w-[calc(100%+3rem)] -ml-4 md:-ml-6">
                    <div className="w-full bg-[#e8d080] rounded-t-[12px] pt-4 pb-4 px-4 md:pt-5 md:pb-5 md:px-6">
                        <div className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-white text-lg md:text-xl tracking-[1.80px] md:tracking-[2.00px] mb-4 text-center">
                            正向評論
                        </div>
                        <div className="flex flex-wrap gap-3 mb-4">
                            {categoriesData.positive.map((item, index) => (
                                <div key={index} onClick={() => handleCategoryClick(item.tagName, 'category', 'positive')} className="cursor-pointer">
                                    <Food
                                        tagName={item.tagName}
                                        percentNumber={item.percentNumber}
                                        prop={item.prop}
                                        prop1={item.prop1}
                                        className="![position:relative] ![top:unset] ![left:unset] hover:opacity-80 transition-opacity"
                                        frameClassName="!h-[30px] !mt-9 !w-[170px]"
                                        frameClassNameOverride="!ml-0"
                                    />
                                </div>
                            ))}
                        </div>
                        {/* 建議文字透明框 */}
                        <div className="bg-transparent rounded-lg p-4 flex items-start gap-3 border-2 border-dashed border-[#d9d9d9]">
                            <img src="/img/Layer_1.png" alt="AI Suggestion" className="w-auto h-12 flex-shrink-0" />
                            <div className="flex-1">
                                <span className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-[#000000] text-sm md:text-base tracking-[1.00px] leading-relaxed">
                                    {commentsAnalyze.category.positive_advice || '暫無建議'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 負向評論區塊 */}
                <div className="w-[calc(100%+2rem)] md:w-[calc(100%+3rem)] -ml-4 md:-ml-6">
                    <div className="w-full bg-[#929084] rounded-b-[12px] pt-4 pb-4 px-4 md:pt-5 md:pb-5 md:px-6">
                        <div className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-white text-lg md:text-xl tracking-[1.80px] md:tracking-[2.00px] mb-4 text-center">
                            負向評論
                        </div>
                        <div className="flex flex-wrap gap-3 mb-4">
                            {categoriesData.negative.map((item, index) => (
                                <div key={index} onClick={() => handleCategoryClick(item.tagName, 'category', 'negative')} className="cursor-pointer">
                                    <Food
                                        tagName={item.tagName}
                                        percentNumber={item.percentNumber}
                                        prop={item.prop}
                                        prop1={item.prop1}
                                        className="![position:relative] ![top:unset] ![left:unset] hover:opacity-80 transition-opacity !bg-[#929084] [&>div:last-child_*]:!text-white"
                                        frameClassName="!h-[30px] !mt-9 !w-[170px]"
                                        frameClassNameOverride="!ml-0"
                                    />
                                </div>
                            ))}
                        </div>
                        {/* 建議文字透明框 */}
                        <div className="bg-transparent rounded-lg p-4 flex items-start gap-3 border-2 border-dashed border-[#d9d9d9]">
                            <img src="/img/Layer_1.png" alt="AI Suggestion" className="w-auto h-12 flex-shrink-0" />
                            <div className="flex-1">
                                <span className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-white text-sm md:text-base tracking-[1.00px] leading-relaxed">
                                    {commentsAnalyze.category.negative_advice || '暫無建議'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 關鍵字區域 */}
            <div className="inline-flex flex-col items-start justify-start px-4 md:px-6 py-4 md:py-6 relative w-full max-w-[1140px] bg-[#eaeade] rounded-[15px] md:rounded-[20px] overflow-hidden">
                {/* 標題和註解 */}
                <div className="flex items-start justify-between w-full gap-3 mb-4">
                    <div className="flex items-center gap-3 md:gap-[11px]">
                        <div className="relative w-[36px] h-[36px] md:w-[46px] md:h-[46px] flex-shrink-0">
                            <img
                                className="absolute w-[73.32%] h-[50.00%] top-[25.00%] left-[12.50%]"
                                alt="Subtract"
                                src="/img/subtract.svg"
                            />
                        </div>
                        <div className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-[#373737] text-xl md:text-[28px] tracking-[2.00px] md:tracking-[2.80px]">
                            關鍵字
                        </div>
                    </div>
                    <div className="flex items-start gap-2 flex-1 justify-end">
                        <button
                            onClick={() => setShowKeywordNote(!showKeywordNote)}
                            className="flex-shrink-0 w-5 h-5 hover:opacity-70 transition-opacity mt-1"
                            aria-label="顯示註解"
                        >
                            <img src="/img/🛈.png" alt="資訊" className="w-full h-full" />
                        </button>
                        {showKeywordNote && (
                            <div className="[font-family:'Noto_Sans_TC',Helvetica] font-normal text-[#666666] text-xs md:text-sm tracking-[0.80px] md:tracking-[1.00px] leading-relaxed bg-transparent max-w-[600px] line-clamp-2">
                                同則評論內容可能包含多個關鍵字,因此各關鍵字的則數為獨立計算 範例:「新鮮80則」=在全部評論中,有80則評論曾提到「新鮮」。
                            </div>
                        )}
                    </div>
                </div>

                {/* 正向關鍵字區塊 */}
                <div className="w-[calc(100%+2rem)] md:w-[calc(100%+3rem)] -ml-4 md:-ml-6">
                    <div className="w-full bg-[#aeceb7] rounded-t-[12px] pt-4 pb-4 px-4 md:pt-5 md:pb-5 md:px-6">
                        <div className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-white text-lg md:text-xl tracking-[1.80px] md:tracking-[2.00px] mb-4 text-center">
                            正向關鍵字
                        </div>
                        <div className="flex flex-wrap gap-3 mb-4">
                            {keywordsData.positive.map((item, index) => (
                                <div key={index} onClick={() => handleCategoryClick(item.tagName, 'keywords', 'positive')} className="cursor-pointer">
                                    <Food
                                        tagName={item.tagName}
                                        percentNumber={item.percentNumber}
                                        prop={item.prop}
                                        prop1={item.prop1}
                                        className="![position:relative] ![top:unset] ![left:unset] hover:opacity-80 transition-opacity !bg-[#aeceb7]"
                                        frameClassName="!h-[30px] !mt-9 !w-[170px]"
                                        frameClassNameOverride="!ml-0"
                                    />
                                </div>
                            ))}
                        </div>
                        {/* 建議文字透明框 */}
                        <div className="bg-transparent rounded-lg p-4 flex items-start gap-3 border-2 border-dashed border-[#d9d9d9]">
                            <img src="/img/Layer_1.png" alt="AI Suggestion" className="w-auto h-12 flex-shrink-0" />
                            <div className="flex-1">
                                <span className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-[#000000] text-sm md:text-base tracking-[1.00px] leading-relaxed">
                                    {commentsAnalyze.keywords.positive_advice || '暫無建議'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 負向關鍵字區塊 */}
                <div className="w-[calc(100%+2rem)] md:w-[calc(100%+3rem)] -ml-4 md:-ml-6">
                    <div className="w-full bg-[#929084] rounded-b-[12px] pt-4 pb-4 px-4 md:pt-5 md:pb-5 md:px-6">
                        <div className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-white text-lg md:text-xl tracking-[1.80px] md:tracking-[2.00px] mb-4 text-center">
                            負向關鍵字
                        </div>
                        <div className="flex flex-wrap gap-3 mb-4">
                            {keywordsData.negative.map((item, index) => (
                                <div key={index} onClick={() => handleCategoryClick(item.tagName, 'keywords', 'negative')} className="cursor-pointer">
                                    <Food
                                        tagName={item.tagName}
                                        percentNumber={item.percentNumber}
                                        prop={item.prop}
                                        prop1={item.prop1}
                                        className="![position:relative] ![top:unset] ![left:unset] hover:opacity-80 transition-opacity !bg-[#929084] [&>div:last-child_*]:!text-white"
                                        frameClassName="!h-[30px] !mt-9 !w-[170px]"
                                        frameClassNameOverride="!ml-0"
                                    />
                                </div>
                            ))}
                        </div>
                        {/* 建議文字透明框 */}
                        <div className="bg-transparent rounded-lg p-4 flex items-start gap-3 border-2 border-dashed border-[#d9d9d9]">
                            <img src="/img/Layer_1.png" alt="AI Suggestion" className="w-auto h-12 flex-shrink-0" />
                            <div className="flex-1">
                                <span className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-white text-sm md:text-base tracking-[1.00px] leading-relaxed">
                                    {commentsAnalyze.keywords.negative_advice || '暫無建議'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 圖表區域：星級百分比 & 情緒百分比 - 响应式 */}
            <div className="flex flex-col md:flex-row gap-4 md:gap-6 w-full max-w-[1190px] border-b-2 md:border-b-[3px] [border-bottom-style:dashed] border-white pb-4 md:pb-6">
                {/* 星級百分比 - 响应式 */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 md:gap-3 mb-2">
                        <img
                            className="w-[28px] h-[28px] md:w-[35px] md:h-[35px]"
                            alt="Star"
                            src="/img/star-1.svg"
                        />
                        <div className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-[#373737] text-lg md:text-2xl tracking-[1.80px] md:tracking-[2.40px]">
                            評論星級百分比
                        </div>
                    </div>
                    <div className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-[#373737] text-xs md:text-sm tracking-[1.00px] md:tracking-[1.40px] mb-3 md:mb-4 ml-8 md:ml-12">
                        Review Star Rating Percentage
                    </div>
                    <div className="w-full h-[280px] md:h-[320px] bg-[#eaeade] rounded-[15px] md:rounded-[20px] overflow-hidden p-3 md:p-4">
                        <StarRatingChart data={commentsAnalyze.average.comments} totalComments={overview.commentsTotal} />
                    </div>
                </div>

                {/* 情緒百分比 - 响应式 */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 md:gap-3 mb-2">
                        <div className="w-[28px] h-[28px] md:w-[35px] md:h-[35px] relative">
                            <div className="absolute w-[86.43%] h-[86.43%] top-[6.79%] left-[6.79%] rounded-[12px] md:rounded-[15.12px] border-2 border-solid border-lineicon" />
                            <img
                                className="absolute w-[33.33%] h-[16.67%] top-[50.00%] left-[33.33%]"
                                alt="Ellipse"
                                src="/img/ellipse-90.svg"
                            />
                            <div className="absolute w-[11.19%] h-[11.19%] top-[36.07%] left-[31.90%] bg-lineicon rounded-[1.5px] md:rounded-[1.96px] border-[0.5px] border-solid border-lineicon" />
                            <div className="absolute w-[11.19%] h-[11.19%] top-[36.07%] left-[56.90%] bg-lineicon rounded-[1.5px] md:rounded-[1.96px] border-[0.5px] border-solid border-lineicon" />
                        </div>
                        <div className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-[#373737] text-lg md:text-2xl tracking-[1.80px] md:tracking-[2.40px]">
                            評論情緒百分比
                        </div>
                    </div>
                    <div className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-[#373737] text-xs md:text-sm tracking-[1.00px] md:tracking-[1.40px] mb-3 md:mb-4 ml-8 md:ml-12">
                        Review Sentiment Percentage
                    </div>
                    <div className="w-full h-[280px] md:h-[320px] bg-[#eaeade] rounded-[15px] md:rounded-[20px] overflow-hidden p-3 md:p-4">
                        <SentimentChart
                            positive={commentsAnalyze.average.sentiment.positive}
                            negative={commentsAnalyze.average.sentiment.negative}
                        />
                    </div>
                </div>
            </div>

            {/* 評論情緒變化趨勢 - 响应式 */}
            <div className="w-full max-w-[1190px] bg-[#eaeade] rounded-[15px] md:rounded-[20px] p-4 md:p-6">
                <div className="flex flex-col md:flex-row items-start md:items-center md:justify-between gap-3 md:gap-0 mb-4">
                    <div className="flex items-center gap-2 md:gap-3">
                        <div className="w-[55px] h-[55px] md:w-[55px] md:h-[55px] relative">
                            <img
                                className="absolute w-[33.33%] h-[41.67%] top-[37.50%] left-[29.17%]"
                                alt="Vector"
                                src="/img/vector-8.svg"
                            />
                            <div className="absolute w-[80.71%] h-[80.71%] top-[9.64%] left-[9.64%] rounded-sm border-2 border-solid border-lineicon" />
                        </div>
                        <div className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-[#373737] text-lg md:text-2xl tracking-[1.80px] md:tracking-[2.40px]">
                            評論情緒變化趨勢
                        </div>
                    </div>
                    <div className="flex items-center gap-3 md:gap-4">
                        <div className="flex items-center gap-1.5 md:gap-2">
                            <div className="w-[16px] h-[16px] md:w-[19px] md:h-[19px] bg-[#7da387] rounded-[8px] md:rounded-[9.5px]" />
                            <div className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-[#373737] text-xs md:text-sm tracking-[1.00px] md:tracking-[1.40px]">
                                正向評論
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5 md:gap-2">
                            <div className="w-[16px] h-[16px] md:w-[19px] md:h-[19px] bg-[#929084] rounded-[8px] md:rounded-[9.5px]" />
                            <div className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-[#373737] text-xs md:text-sm tracking-[1.00px] md:tracking-[1.40px]">
                                負向評論
                            </div>
                        </div>
                    </div>
                </div>
                <div className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-[#373737] text-xs md:text-sm tracking-[1.00px] md:tracking-[1.40px] mb-3 md:mb-4 ml-8 md:ml-12">
                    Review Sentiment Trend
                </div>
                <div className="w-full h-[320px] md:h-[440px] bg-white rounded-[15px] md:rounded-[20px] overflow-hidden p-3 md:p-4">
                    <TrendChart
                        positive={commentsAnalyze.lineChart.positive}
                        negative={commentsAnalyze.lineChart.negative}
                    />
                </div>
            </div>
        </div>
    )
}
